import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "src/hooks/useAuth";
import useLocales from "src/hooks/useLocales";
import { useResetPassword } from "src/services/auth/reset_password";
import { removeHeaderToken } from "src/services/config/apiClient";
import { PATH_AUTH } from "src/utils/paths";
import SnackbarUtils from "src/utils/SnackbarUtils";
import * as Yup from "yup";

import Context from "../context";

export type ChangePasswordFormValues = {
	newPassword: string;
	confirmNewPassword: string;
};

export const useChangePasswordForm = () => {
	const [isLoading, setIsLoading] = useState(false);
	const { translate } = useLocales();

	const { mutate } = useResetPassword();
	const { token } = useContext(Context);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { signOut } = useAuth();

	const ChangePasswordSchema = Yup.object().shape({
		newPassword: Yup.string()
			.required(translate("Contraseña nueva requerida"))
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-8])(?=.*[!@#$%^&*(),.?":{}|<>])(?!.*(.)\1{3})(?=.{8,30}$)/,
				translate(
					"La nueva contraseña debe tener al menos 8 caracteres, una letra mayúscula, un número, un caracter especial y no repetir la misma letra más de tres veces."
				)
			),
		confirmNewPassword: Yup.string()
			.required(translate("Contraseña nueva requerida"))
			.oneOf([Yup.ref("newPassword")], "Las contraseñas deben coincidir")
	});

	const defaultValues: ChangePasswordFormValues = {
		newPassword: "",
		confirmNewPassword: ""
	};

	const methods = useForm<ChangePasswordFormValues>({
		resolver: yupResolver(ChangePasswordSchema),
		defaultValues
	});

	const handleLogout = () => {
		removeHeaderToken();
		signOut();
		queryClient.removeQueries();
		setTimeout(() => navigate({ to: PATH_AUTH.login, replace: true }), 300);
	};

	const onSuccess = () => {
		setIsLoading(false);
		SnackbarUtils.success("¡Contraseña cambiada con éxito!");
		handleLogout();
	};

	const onError = (response: any) => {
		if (response.data && (response.data === "AUTH_04" || response.data === "Token not valid")) {
			SnackbarUtils.error(
				"El token de recuperación ha expirado, por favor vuelva a la pantalla de recuperar contraseña."
			);
		} else {
			SnackbarUtils.error("Se ha producido un error, por favor vuelve a intentarlo más tarde");
		}
		setIsLoading(false);
	};

	const onSubmit = async (data: ChangePasswordFormValues) => {
		setIsLoading(true);
		mutate({ newPassword: data.newPassword, token }, { onSuccess, onError });
	};

	return {
		methods,
		isLoading,
		onSubmit: methods.handleSubmit(onSubmit)
	};
};
